import { EmptyState } from '@/components/EmptyState';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import type { TaskNotification } from '@shared/types/tasks';
import { isConfirmDialogOpen } from '@/components/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { usePermission } from '@/hooks/usePermission';
import { markTaskNotificationsRead } from '@/lib/tasks-api';
import { cn } from '@/lib/utils';
import { formatRelative } from './task-ui';
import { useTaskNotifications } from './useTasks';
import { CountBadge } from '@/components/CountBadge';

const VERB: Record<TaskNotification['kind'], string> = {
  assigned: 'assigned you',
  mentioned: 'mentioned you',
  chat: 'wrote',
  moved: 'finished',
  review: 'asked you to review',
  unsnoozed: 'Back from snooze:',
  repeated: 'Came round again:',
  waiting: 'is waiting on you for',
  replied: 'replied on',
  unblocked: 'Back with you:',
};

/** Top-bar bell: what happened on your tasks. Live through the task socket. */
export function NotificationBell() {
  const canView = usePermission('TASKS');
  const [open, setOpen] = useState(false);
  const { notifications, unread, setNotifications, setUnread } = useTaskNotifications(canView);

  if (!canView) return null;

  // Runs as the row's link is followed, so a cancelled leave question marks nothing read.
  const markOpened = (n: TaskNotification) => {
    setOpen(false);
    if (!n.read) {
      setNotifications(notifications.map((x) => (x.task_id === n.task_id ? { ...x, read: true } : x)));
      setUnread(Math.max(0, unread - notifications.filter((x) => x.task_id === n.task_id && !x.read).length));
      void markTaskNotificationsRead({ task_id: n.task_id });
    }
  };

  const markAll = () => {
    setNotifications(notifications.map((x) => ({ ...x, read: true })));
    setUnread(0);
    void markTaskNotificationsRead();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon-sm" className="relative" aria-label={`Notifications${unread ? `: ${unread} unread` : ''}`}>
          <Bell />
          <CountBadge count={unread} max={9} corner />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-0"
        onFocusOutside={(e) => {
          // The leave question must not close the bell, or Leave would have no link to follow.
          if (isConfirmDialogOpen()) e.preventDefault();
        }}
      >
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-semibold">Notifications</span>
          {unread ? (
            <Button variant="ghost" size="compact" className="text-xs" onClick={markAll}>
              Mark all read
            </Button>
          ) : null}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {notifications.length === 0 ? (
            <EmptyState size="compact" title="No notifications yet" />
          ) : (
            notifications.map((n) => (
              <Link
                key={n.id}
                to={`/tasks/${n.task_id}`}
                onClick={() => markOpened(n)}
                className={cn(
                  'flex w-full gap-2 border-b px-3 py-2 text-left text-sm outline-none last:border-0 hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                  !n.read && 'bg-primary/5'
                )}
              >
                <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', n.read ? 'bg-transparent' : 'bg-primary')} />
                <span className="min-w-0 flex-1">
                  {n.kind === 'unsnoozed' || n.kind === 'repeated' || n.kind === 'unblocked' ? (
                    // No one did this: the snooze ended, the Repeat came round, or the task it waited on is done (T-331).
                    <span className="block">
                      {VERB[n.kind]} <span className="font-medium">{n.task_title}</span>
                    </span>
                  ) : (
                    <span className="block">
                      <span className="font-medium">{n.actor_name ?? 'Someone'}</span>
                      {n.via_api_key_name ? <span className="text-muted-foreground"> (via {n.via_api_key_name})</span> : null}{' '}
                      {VERB[n.kind]}
                      {n.kind === 'chat' || n.kind === 'mentioned' ? ' on' : ''} <span className="font-medium">{n.task_title}</span>
                    </span>
                  )}
                  {n.excerpt && n.kind !== 'assigned' && n.kind !== 'review' ? (
                    <span className="line-clamp-2 text-xs text-muted-foreground">{n.excerpt}</span>
                  ) : null}
                  <span className="text-xs text-muted-foreground">{formatRelative(n.created_at)}</span>
                </span>
              </Link>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
